import {
  imageGenerationResultSchema,
  textGenerationResultSchema,
  videoGenerationResultSchema,
  type AIEvent,
  type AIGeneration,
  type AIGenerationStatus,
  type ImageGenerationResult,
  type TextGenerationResult,
  type VideoGenerationResult,
} from '@recipes/contracts';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useState } from 'react';
import { api } from '@/lib/api';
import { queryKeys } from '@/lib/query-keys';
import { useSse } from '@/lib/sse/use-sse';

const TERMINAL: AIGenerationStatus[] = ['SUCCEEDED', 'FAILED', 'CANCELLED'];

export type ParsedGenerationResult =
  | { kind: 'images'; result: ImageGenerationResult }
  | { kind: 'text'; result: TextGenerationResult }
  | { kind: 'video'; result: VideoGenerationResult }
  | null;

export function parseGenerationResult(result: unknown): ParsedGenerationResult {
  if (!result) return null;
  const image = imageGenerationResultSchema.safeParse(result);
  if (image.success) return { kind: 'images', result: image.data };
  // Before text: a video result is a text result with the video's link.
  const video = videoGenerationResultSchema.safeParse(result);
  if (video.success) return { kind: 'video', result: video.data };
  const text = textGenerationResultSchema.safeParse(result);
  if (text.success) return { kind: 'text', result: text.data };
  return null;
}

/**
 * Loads an AI generation and keeps it fresh: SSE first (status/result/error events),
 * polling every 2 s as a fallback while the generation is still running.
 */
export function useGeneration(generationId: string | null) {
  const queryClient = useQueryClient();
  const [streamError, setStreamError] = useState<{ code: string; message: string } | null>(null);
  const [liveProgress, setLiveProgress] = useState<number | null>(null);
  const [questions, setQuestions] = useState<string[] | null>(null);

  const query = useQuery({
    queryKey: queryKeys.ai.generation(generationId ?? ''),
    queryFn: () => api.ai.generation(generationId ?? ''),
    enabled: Boolean(generationId),
  });
  const generation = query.data ?? null;
  const isTerminal = generation ? TERMINAL.includes(generation.status) : false;

  const onEvent = useCallback(
    (event: AIEvent) => {
      if (!generationId) return;
      const key = queryKeys.ai.generation(generationId);
      switch (event.type) {
        case 'status':
          setLiveProgress(event.progress);
          // A terminal status ends the stream – possibly before the `result` event was read.
          // Marking the generation SUCCEEDED here would leave it without a result for good;
          // fetch the finished generation instead.
          if (TERMINAL.includes(event.status)) {
            void queryClient.invalidateQueries({ queryKey: key });
            break;
          }
          queryClient.setQueryData<AIGeneration>(key, (prev) =>
            prev ? { ...prev, status: event.status, progress: event.progress } : prev,
          );
          break;
        case 'question':
          setQuestions(event.questions);
          break;
        case 'result':
          queryClient.setQueryData<AIGeneration>(key, (prev) =>
            prev ? { ...prev, status: 'SUCCEEDED', progress: 100, result: event.result } : prev,
          );
          break;
        case 'error':
          setStreamError({ code: event.code, message: event.message });
          queryClient.setQueryData<AIGeneration>(key, (prev) =>
            prev ? { ...prev, status: 'FAILED', error: event.message } : prev,
          );
          break;
        case 'done':
          void queryClient.invalidateQueries({ queryKey: key });
          break;
        default:
          break;
      }
    },
    [generationId, queryClient],
  );

  const sse = useSse<AIEvent>(
    generationId && !isTerminal ? api.ai.generationEventsUrl(generationId) : null,
    {
      onEvent,
      isTerminal: (e) =>
        e.type === 'done' ||
        e.type === 'error' ||
        (e.type === 'status' && TERMINAL.includes(e.status)),
    },
  );

  const polling =
    Boolean(generationId) && !isTerminal && (sse.status === 'error' || sse.status === 'closed');
  useEffect(() => {
    if (!polling || !generationId) return;
    const id = setInterval(
      () => void queryClient.invalidateQueries({ queryKey: queryKeys.ai.generation(generationId) }),
      2000,
    );
    return () => clearInterval(id);
  }, [polling, generationId, queryClient]);

  return {
    generation,
    isLoading: query.isLoading,
    error: query.error,
    streamError,
    progress: liveProgress ?? generation?.progress ?? 0,
    questions,
    parsed: generation?.status === 'SUCCEEDED' ? parseGenerationResult(generation.result) : null,
    polling,
    refetch: query.refetch,
  };
}
